import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#onSubmenuOpenClick, true);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onSubmenuOpenClick, true);
  }

  /**
   * Capture click on main menu item (HIGHLIGHTS, WOMEN, etc.) to position submenu title.
   * Runs in capture phase so we get the correct clicked element before other handlers.
   */
  #onSubmenuOpenClick = (event) => {
    if (window.innerWidth >= 750) return;

    const summary = event.target.closest('summary');
    if (!summary) return;
    if (!summary.closest('.menu-drawer__menu-container')) return;

    const details = summary.closest('details');
    const submenu = details?.querySelector('.menu-drawer__submenu');
    if (!submenu) return;

    const drawer = summary.closest('.menu-drawer');
    if (!(drawer instanceof HTMLElement)) return;

    /* Same Y as tapped row: slide already has padding-top var(--menu-drawer-content-start), so margin-top only fills the gap below that padding. */
    const pushRaw = getComputedStyle(drawer).getPropertyValue('--menu-drawer-content-start').trim();
    const pushPx = Number.parseFloat(pushRaw) || 0;
    const fromDrawerTop = summary.getBoundingClientRect().top - drawer.getBoundingClientRect().top;
    const offsetPx = Math.max(0, Math.round(fromDrawerTop - pushPx));
    submenu.style.setProperty('--submenu-title-offset', `${offsetPx}px`);
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event] - Passed by component system; used to preventDefault when closing so browser doesn't remove [open] before our keyframe animation completes.
   */
  toggle(event) {
    if (this.isOpen) {
      if (event && typeof event.preventDefault === 'function') {
        event.preventDefault();
      }
      return this.close();
    }
    return this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    // On mobile: align drawer/backdrop with theme offset from top of viewport (see --drawer-viewport-top)
    if (window.innerWidth < 750 && details === this.refs.details) {
      const raw = getComputedStyle(document.documentElement).getPropertyValue('--drawer-viewport-top').trim();
      details.style.setProperty('--menu-drawer-top', raw || '175px');
    }

    this.preventInitialAccordionAnimations(details);

    const addMenuOpenAndSetup = () => {
      details.classList.add('menu-open');
      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus.
      // Submenus: slide has the transition, so we must wait on it (not the container).
      const drawer = details.querySelector('.menu-drawer__submenu-slide') ?? details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    };

    if (target) {
      requestAnimationFrame(() => {
        requestAnimationFrame(addMenuOpenAndSetup);
      });
    } else {
      requestAnimationFrame(addMenuOpenAndSetup);
    }
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    const isMainMenu = details === this.refs.details;

    if (isMainMenu && details.classList.contains('menu-closing')) {
      return;
    }

    summary.setAttribute('aria-expanded', 'false');

    if (isMainMenu && window.innerWidth < 750) {
      details.classList.add('menu-closing');
      const drawer = this.refs.menuDrawer;
      drawer.style.animation = 'none';
      void drawer.offsetWidth;
      drawer.style.animation = '';
      requestAnimationFrame(() => {
        onAnimationEnd(drawer, () => {
          details.classList.remove('menu-closing');
          reset(details);
          removeTrapFocus();
          this.querySelectorAll('details[open]:not(accordion-custom > details)').forEach(reset);
        }, { subtree: false });
      });
    } else {
      details.classList.remove('menu-open');
      this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
      const drawer = details.querySelector('.menu-drawer__submenu-slide') ?? details.querySelector('.menu-drawer, .menu-drawer__submenu');
      requestAnimationFrame(() => {
        onAnimationEnd(drawer || details, () => {
          reset(details);
          if (details === this.refs.details) {
            removeTrapFocus();
            this.querySelectorAll('details[open]:not(accordion-custom > details)').forEach(reset);
          } else {
            trapFocus(this.refs.details);
          }
        }, { subtree: false });
      });
    }
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
